import type { ReactNode } from 'react';
import { Globe, Share2 } from 'lucide-react';
import type { Nudge } from './matchStateResolver';

/**
 * The nudge copy + presentation data, split out of NudgeCard.tsx so these non-component values can
 * be shared (question-detail match states AND the event "For you" gates) without tripping
 * react-refresh/only-export-components - the matchCardTitle.ts / buttonVariants.ts precedent, keeping
 * NudgeCard.tsx a components-only HMR boundary. Copy is lowercase meetball tone, hyphens not em
 * dashes; the strings are exported so tests pin against these constants rather than re-typed literals.
 */

// Copy (async-matching UX, issue #22). The support lines are verbatim per the tone-of-voice-reviewed
// base. Each nudge is a designed card that speaks the ProfileCompletionModal's language: a meaning
// glyph, a short warm headline, the support sentence, and a solid teal pill CTA. Headlines are the
// short derived hook; the existing sentence carries the detail underneath.
// Copy says "post", never "question" (Dexter, 2026-10-14).
export const SHARE_NUDGE_COPY = 'this space is still small - share your post in more spaces';
export const JOIN_SPACES_NUDGE_COPY =
  'join spaces and communities to connect and match with more people';
export const PROFILE_EMPTY_NUDGE_COPY =
  'complete your profile to find the people who share your interests and can help';
export const PROFILE_PARTIAL_MISSING_BACKGROUND_COPY =
  'you told us what you can help with - add your professional background for even better matches';
export const PROFILE_PARTIAL_MISSING_CAN_HELP_COPY =
  'you told us your professional background - add what you can help with for even better matches';

// Short warm headlines (the derived hook above each support line).
export const SHARE_NUDGE_HEADLINE = 'reach more people';
export const JOIN_SPACES_NUDGE_HEADLINE = 'find your people';
export const PROFILE_EMPTY_NUDGE_HEADLINE = 'let the right people find you';
export const PROFILE_PARTIAL_NUDGE_HEADLINE = "you're almost there";

// CTA labels (lowercase meetball voice, hyphens only).
export const SHARE_NUDGE_CTA = 'share your post';
export const JOIN_SPACES_NUDGE_CTA = 'find spaces';
export const PROFILE_EMPTY_NUDGE_CTA = 'complete your profile';
export const PROFILE_PARTIAL_NUDGE_CTA = 'add it now';

// The meaning glyphs are static JSX (not a component alias built in render) so they stay stable.
// Presented in the ProfileCompletionModal's icon slot (size-10.5). Lucide glyphs take a teal tint so
// they sit with the CTA + the teal end of the gradient border; the profile nudges reuse the modal's
// own designed icon (same meaning, one shared asset - no green line glyph). Exported so surfaces that
// build their own nudge (the event "For you" gates) apply the identical glyph treatment.
export const NUDGE_GLYPH_CLASS = 'size-10.5 text-mb-teal';
export const profileNudgeIcon = (
  <img src="/assets/complete_profile_icon.svg" alt="" className="size-10.5" />
);

export interface NudgePresentation {
  headline: string;
  ctaLabel: string;
  icon: ReactNode;
  /** Support sentence. A function of the nudge because the profile_partial body depends on WHICH
   *  field is missing; the type-only nudges ignore the argument. */
  body: (nudge: Nudge) => string;
}

/**
 * One presentation record keyed by nudge type - the single source for each nudge's headline, body,
 * CTA label, and meaning glyph (replacing four parallel branch-on-type helpers). Copy strings are
 * byte-identical to the exported constants the tests pin. Actions (navigation / share) stay in the
 * consuming component's click handler, since they close over router + props rather than presentation.
 */
export const NUDGE_PRESENTATION: Record<Nudge['type'], NudgePresentation> = {
  share: {
    headline: SHARE_NUDGE_HEADLINE,
    ctaLabel: SHARE_NUDGE_CTA,
    icon: <Share2 className={NUDGE_GLYPH_CLASS} aria-hidden="true" />,
    body: () => SHARE_NUDGE_COPY,
  },
  join_spaces: {
    headline: JOIN_SPACES_NUDGE_HEADLINE,
    ctaLabel: JOIN_SPACES_NUDGE_CTA,
    icon: <Globe className={NUDGE_GLYPH_CLASS} aria-hidden="true" />,
    body: () => JOIN_SPACES_NUDGE_COPY,
  },
  profile_empty: {
    headline: PROFILE_EMPTY_NUDGE_HEADLINE,
    ctaLabel: PROFILE_EMPTY_NUDGE_CTA,
    icon: profileNudgeIcon,
    body: () => PROFILE_EMPTY_NUDGE_COPY,
  },
  profile_partial: {
    headline: PROFILE_PARTIAL_NUDGE_HEADLINE,
    ctaLabel: PROFILE_PARTIAL_NUDGE_CTA,
    icon: profileNudgeIcon,
    body: nudge =>
      nudge.type === 'profile_partial' && nudge.missingField === 'professional_background'
        ? PROFILE_PARTIAL_MISSING_BACKGROUND_COPY
        : PROFILE_PARTIAL_MISSING_CAN_HELP_COPY,
  },
};
